import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, Leaf, Shield, Truck } from 'lucide-react';
import { Button } from './ui/button';
import { heroData } from '../data/mock';

export const Hero = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [scrollY, setScrollY] = useState(0);
  const heroRef = useRef(null);

  // Mouse parallax effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left - rect.width / 2) / rect.width;
        const y = (e.clientY - rect.top - rect.height / 2) / rect.height;
        setMousePosition({ x, y });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Scroll parallax effect
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Generate floating leaves
  const floatingLeaves = Array.from({ length: 15 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    animationDelay: Math.random() * 5,
    duration: 10 + Math.random() * 10,
    size: 20 + Math.random() * 30
  }));

  return (
    <section 
      id="hero" 
      ref={heroRef}
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Static Background Image */}
      <div className="absolute inset-0 z-0">
        <img 
          src={heroData.image} 
          alt="Zago Pallets - Tarimas de Madera"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/70 to-gray-900/50"></div>
      </div>

      {/* Floating Leaves Animation */}
      <div className="absolute inset-0 z-5 pointer-events-none">
        {floatingLeaves.map((leaf) => (
          <div
            key={leaf.id}
            className="absolute text-green-400/30"
            style={{
              left: `${leaf.left}%`,
              animationDelay: `${leaf.animationDelay}s`,
              animation: `floatLeaf ${leaf.duration}s infinite ease-in-out`,
              fontSize: `${leaf.size}px`
            }}
          >
            <Leaf className="animate-spin-slow" style={{ animationDuration: '20s' }} />
          </div>
        ))}
      </div>

      {/* Gradient Orbs for depth */}
      <div className="absolute inset-0 z-5 pointer-events-none">
        <div 
          className="absolute w-96 h-96 bg-green-500/20 rounded-full blur-3xl"
          style={{
            top: '20%',
            left: '10%',
            transform: `translate(${mousePosition.x * -30}px, ${mousePosition.y * -30}px)`
          }}
        ></div>
        <div 
          className="absolute w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl"
          style={{
            bottom: '20%',
            right: '10%',
            transform: `translate(${mousePosition.x * 40}px, ${mousePosition.y * 40}px)`
          }}
        ></div>
      </div>

      {/* Static Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="max-w-3xl">
          {/* Badge */}
          <div className="inline-flex items-center space-x-2 bg-green-600/20 backdrop-blur-sm border border-green-500/30 rounded-full px-4 py-2 mb-6">
            <Leaf className="w-4 h-4 text-green-400 animate-pulse" />
            <span className="text-green-300 text-sm font-medium">100% Eco-Friendly</span>
          </div>

          {/* Main Heading */}
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            {heroData.title}
          </h1>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-green-400 mb-6">
            {heroData.subtitle}
          </h2>
          
          {/* Description */}
          <p className="text-xl text-gray-200 mb-8 leading-relaxed">
            {heroData.description}
          </p>

          {/* CTAs with hover effects */}
          <div className="flex flex-col sm:flex-row gap-4 mb-12">
            <Button 
              size="lg"
              onClick={() => scrollToSection('contacto')}
              className="group bg-green-700 hover:bg-green-800 text-white text-lg px-8 py-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 relative overflow-hidden"
            >
              <span className="relative z-10 flex items-center">
                {heroData.cta}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-green-600 to-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => scrollToSection('productos')}
              className="group bg-white/10 backdrop-blur-sm border-white/30 text-white hover:bg-white/20 text-lg px-8 py-6 hover:scale-105 transition-all duration-300 hover:border-white/50"
            >
              {heroData.ctaSecondary}
            </Button>
          </div>

          {/* Features with hover animations only */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="group flex items-center space-x-3 bg-white/10 backdrop-blur-sm rounded-lg p-4 hover:bg-white/20 transition-all duration-300 cursor-pointer hover:scale-105">
              <div className="w-12 h-12 bg-green-600/30 rounded-lg flex items-center justify-center group-hover:bg-green-600/50 transition-colors group-hover:rotate-12 transition-transform duration-300">
                <Leaf className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-white font-semibold">Recicladas</p>
                <p className="text-gray-300 text-sm">Sustentables</p>
              </div>
            </div>
            
            <div className="group flex items-center space-x-3 bg-white/10 backdrop-blur-sm rounded-lg p-4 hover:bg-white/20 transition-all duration-300 cursor-pointer hover:scale-105">
              <div className="w-12 h-12 bg-green-600/30 rounded-lg flex items-center justify-center group-hover:bg-green-600/50 transition-colors group-hover:rotate-12 transition-transform duration-300">
                <Shield className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-white font-semibold">NOM-144 SEMARNAT</p>
                <p className="text-gray-300 text-sm">Certificación HT</p>
              </div>
            </div>
            
            <div className="group flex items-center space-x-3 bg-white/10 backdrop-blur-sm rounded-lg p-4 hover:bg-white/20 transition-all duration-300 cursor-pointer hover:scale-105">
              <div className="w-12 h-12 bg-green-600/30 rounded-lg flex items-center justify-center group-hover:bg-green-600/50 transition-colors group-hover:rotate-12 transition-transform duration-300">
                <Truck className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-white font-semibold">Entrega Rápida</p>
                <p className="text-gray-300 text-sm">Monterrey, NL</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Animated Scroll Indicator */}
      <div 
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce cursor-pointer group"
        onClick={() => scrollToSection('servicios')}
      >
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2 group-hover:border-green-400 transition-colors">
          <div className="w-1 h-3 bg-white/70 rounded-full group-hover:bg-green-400 transition-colors"></div>
        </div>
      </div>

      <style>{`
        @keyframes floatLeaf {
          0% {
            transform: translateY(-100vh) rotate(0deg);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(360deg);
            opacity: 0;
          }
        }

        @keyframes fade-in-down {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in-down {
          animation: fade-in-down 0.8s ease-out both;
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out both;
        }

        .animate-spin-slow {
          animation: spin 20s linear infinite;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </section>
  );
};
